import { normalizeStringArray } from "@garimpa/lib/normalizeStringArray";

/**
 * Raw search-param values arrive decoded by the router's URLSearchParams codec:
 * scalar values are strings, repeated flat params are arrays, and recursive
 * filters travel as dot-path objects in the `filter` param.
 * The pickers below accept those shapes so a `validateSearch` schema can hand
 * untrusted user URLs or app-built URLs interchangeably.
 */
type RawParam = unknown;

/** Pick a validated enum value from a search param, with optional fallback. */
export function pickEnum<T extends string>(value: RawParam, valid: readonly T[]): T | undefined;
export function pickEnum<T extends string>(value: RawParam, valid: readonly T[], fallback: T): T;
export function pickEnum<T extends string>(
  value: RawParam,
  valid: readonly T[],
  fallback?: T,
): T | undefined {
  const s = typeof value === "string" ? value : undefined;
  return s && (valid as readonly string[]).includes(s) ? (s as T) : fallback;
}

/** Pick a string search param. Coerces numbers to their string form. */
export function pickString(value: RawParam): string | undefined {
  if (typeof value === "string") return value || undefined;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

/** Pick a numeric search param. Accepts both coerced numbers and numeric strings. */
export function pickNumber(value: RawParam): number | undefined {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** Pick a boolean search param. Accepts both coerced booleans and true/false strings. */
export function pickBoolean(value: RawParam): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

/**
 * Pick a string-array search param. Accepts repeated params or a single string
 * value. Commas are preserved because catalog labels/company names may contain
 * punctuation.
 */
export function pickStringArray(value: RawParam): string[] | undefined {
  const parts = normalizeStringArray(value);
  return parts.length ? parts : undefined;
}
